import { useCallback, useEffect, useRef, useState } from 'react';
import type { DocumentNode } from 'graphql';
import type { JWMView } from '@atlassian/jira-business-common/src/common/types/jwm-view.tsx';
import { getVariables } from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import type { ExperienceDetails } from '@atlassian/jira-business-experience-tracking/src/types.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { getPreviousRank, getNextRank } from '@atlassian/jira-business-rank/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { ChildrenQueryData } from '../children/types.tsx';
import { useCacheActions } from '../list-cache/main.tsx';
import type { BaseIssueNode, BaseListData } from '../list-cache/types.tsx';
import { getOptimisticRanked } from '../rank-issue/utils.tsx';
import { useUpdateIssueField } from '../update-issue-field/index.tsx';
import {
	UPDATE_ITEM_PARENT_FAIL_GENERIC_FLAG,
	UPDATE_ITEM_PARENT_FAIL_400_FLAG,
	UPDATE_ITEM_PARENT_FAIL_404_FLAG,
	UPDATE_ITEM_PARENT_FAIL_400_FLAG_WITH_ISSUE_TERM,
	UPDATE_ITEM_PARENT_FAIL_404_FLAG_WITH_ISSUE_TERM,
	getUpdateParentIssueExperience,
	SUCCEEDED,
	FAILED,
	PACKAGE_NAME,
	ERROR_ID,
	TEAM_NAME,
} from './constants.tsx';
import type {
	UpdateParentAndRankIssueItem,
	UnlinkAndReRankIssueItem,
	RankRequestType,
	ViewIssue,
	AddChildToParent,
	RemoveChildFromParent,
	RevertData,
} from './types.tsx';
import { generateIssueWithUpdatedParent } from './utils.tsx';

export interface IViewIssues {
	loading: boolean;
}

export const useUpdateIssueParent = <
	ViewIssuesReturn extends IViewIssues,
	IssueCacheNode extends BaseIssueNode,
	IssueCacheValue extends BaseListData<IssueCacheValue>,
>({
	childrenQueryData = {},
	fieldIds,
	issueParentMap,
	issuesMap,
	cacheQuery,
	addChildToParent,
	removeChildFromParent,
	view,
	packageName,
	shouldOnlyUpdateCache = false,
	projectKey,
}: {
	childrenQueryData?: ChildrenQueryData<ViewIssuesReturn>;
	fieldIds: string[];
	issueParentMap?: Map<string, string>;
	issuesMap: Map<string, ViewIssue>;
	cacheQuery: DocumentNode;
	addChildToParent: AddChildToParent<IssueCacheNode>;
	removeChildFromParent?: RemoveChildFromParent<IssueCacheNode>;
	view: JWMView;
	packageName: ExperienceDetails['packageName'];
	shouldOnlyUpdateCache?: boolean;
	projectKey?: string;
}) => {
	const { showFlag } = useFlagService();
	const { hierarchyLevels } = useIssueTypesAndFields({ issueOperation: 'VIEW' });
	const { quickFiltersJql, hierarchyParent, jqlContext, isHidingDoneItems } =
		useFilteredViewContextInput({
			view,
		});

	const { addToCache, getCachedIssueById, removeFromCache, updateItemsInCache } = useCacheActions();
	const [newParentIssue, setNewParentIssue] = useState<ViewIssue>();
	const [reparentIssue, setReparentIssue] = useState<BaseIssueNode>();
	const [optimisticRank, setOptimisticRank] = useState('');
	const shouldAddToCache = useRef(false);
	const [revertData, setRevertData] = useState<null | RevertData>(null);

	const updateParentIssueExperience = getUpdateParentIssueExperience(packageName);
	const startExperience = useExperienceStart(updateParentIssueExperience);
	const markExperienceSuccess = useExperienceSuccess(updateParentIssueExperience);
	const markExperienceFailed = useExperienceFail(updateParentIssueExperience);

	const updateIssueField = useUpdateIssueField({
		onSuccess: () => {
			markExperienceSuccess();
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onError: (error: any) => {
			if (error.statusCode === 400) {
				showFlag(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? UPDATE_ITEM_PARENT_FAIL_400_FLAG_WITH_ISSUE_TERM
						: UPDATE_ITEM_PARENT_FAIL_400_FLAG,
				);
			} else if (error.statusCode === 404) {
				showFlag(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? UPDATE_ITEM_PARENT_FAIL_404_FLAG_WITH_ISSUE_TERM
						: UPDATE_ITEM_PARENT_FAIL_404_FLAG,
				);
			} else {
				showFlag(UPDATE_ITEM_PARENT_FAIL_GENERIC_FLAG);
				markExperienceFailed('update issue parent failed', error);
			}
			throw error;
		},
	});

	const getRankPosition = useCallback(
		(rankRequest?: RankRequestType) => {
			if (!rankRequest) return;

			const relativeIssueId = rankRequest.rankAfterId || rankRequest.rankBeforeId;
			if (relativeIssueId) {
				const relativeIssue = issuesMap.get(relativeIssueId);

				const relativeIssueRank = rankRequest.rankPosition || relativeIssue?.rank;
				if (relativeIssueRank) {
					const { rankBeforeId } = rankRequest;
					return rankBeforeId ? getPreviousRank(relativeIssueRank) : getNextRank(relativeIssueRank);
				}
			}
		},
		[issuesMap],
	);

	const getVariablesInput = useCallback(
		(parentId?: string) => {
			const defaultVariableInput = {
				jqlContext,
				fieldIds,
				quickFiltersJql,
				isHidingDoneItems,
				...(hierarchyLevels !== undefined && {
					hierarchyLevel: hierarchyLevels[0]?.level,
				}),
				// project clause is only used in List view to render issues with parents from foreign projects
				projectIdOrKey: ff('list-uses-new-jql-parent-project-directive_v5gfs')
					? projectKey
					: undefined,
			};
			if (hierarchyParent && parentId) {
				return {
					...defaultVariableInput,
					hierarchyParent: undefined,
					parentIssue: {
						hierarchyType: hierarchyParent,
						issueId: parentId,
					},
				};
			}
			return {
				...defaultVariableInput,
				hierarchyParent,
			};
		},
		[
			jqlContext,
			fieldIds,
			quickFiltersJql,
			isHidingDoneItems,
			hierarchyLevels,
			projectKey,
			hierarchyParent,
		],
	);

	useEffect(() => {
		if (hierarchyParent && newParentIssue && reparentIssue) {
			const { issueId: newParentId, hasChildren, childSummary } = newParentIssue;
			if (childrenQueryData[newParentId] && childrenQueryData[newParentId].loading === false) {
				const grandParentId = issueParentMap?.get(newParentId) ?? newParentIssue?.parentIssueId;
				if (!hasChildren || typeof childSummary?.totalCount === 'number') {
					// Update parent issue hasChildren to true
					updateItemsInCache<IssueCacheNode, IssueCacheValue>({
						query: cacheQuery,
						// We need to get the details of the current parent from the grandparent
						variables: getVariables(getVariablesInput(grandParentId)),
						issueIds: [newParentIssue.issueId],
						updateItem: (existingIssue) => addChildToParent(existingIssue),
					});
				}
				if (fg('jwm_timeline_meatball_reparent_menu')) {
					if (shouldAddToCache.current) {
						const variables = getVariables(getVariablesInput(newParentId));
						const newNode = generateIssueWithUpdatedParent(reparentIssue, newParentIssue, view);
						addToCache({
							query: cacheQuery,
							variables,
							newNodes: [optimisticRank ? getOptimisticRanked(newNode, optimisticRank) : newNode],
						});
						shouldAddToCache.current = false;
						setReparentIssue(undefined);
						setNewParentIssue(undefined);
						setOptimisticRank('');
					}
				} else {
					const variables = getVariables(getVariablesInput(newParentId));
					const newNode = generateIssueWithUpdatedParent(reparentIssue, newParentIssue, view);
					addToCache({
						query: cacheQuery,
						variables,
						newNodes: [optimisticRank ? getOptimisticRanked(newNode, optimisticRank) : newNode],
					});
					setReparentIssue(undefined);
					setNewParentIssue(undefined);
					setOptimisticRank('');
				}
			}
		}
	}, [
		addToCache,
		childrenQueryData,
		getVariablesInput,
		hierarchyParent,
		isHidingDoneItems,
		jqlContext,
		quickFiltersJql,
		newParentIssue,
		reparentIssue,
		updateItemsInCache,
		getCachedIssueById,
		issueParentMap,
		optimisticRank,
		cacheQuery,
		addChildToParent,
		issuesMap,
		view,
	]);

	const updateIssueParentCache = useCallback(
		({
			oldParentId,
			issueKey,
			parentIssue,
			isMeatballsAction,
		}: {
			oldParentId?: string;
			issueKey: string;
			parentIssue: ViewIssue;
			isMeatballsAction?: boolean;
		}) => {
			const variables = getVariables(getVariablesInput(oldParentId));
			const issue = getCachedIssueById({
				issueId: issueKey,
				query: cacheQuery,
				variables,
			});

			setNewParentIssue(parentIssue);
			if (issue) {
				setReparentIssue(issue);
				// If update parent action is from meatballs menu and new parent is not expanded, don't use optimistic update
				// Otherwise, it will cause the updated issue unmounted and remounted in timeline-table
				// which will cause the focus can't return to meatballs button after the reparent (for keyboard user)
				if (
					isMeatballsAction &&
					!(
						childrenQueryData[parentIssue.issueId] &&
						childrenQueryData[parentIssue.issueId].loading === false
					) &&
					fg('jwm_timeline_meatball_reparent_menu')
				) {
					return;
				}

				// Remove the issue from the old parent
				removeFromCache({
					query: cacheQuery,
					variables,
					issueId: Number(issueKey),
				});

				if (fg('jwm_timeline_meatball_reparent_menu')) {
					if (
						childrenQueryData[parentIssue.issueId] &&
						childrenQueryData[parentIssue.issueId].loading === false
					) {
						const originalRank = issuesMap.get(String(issueKey))?.rank;
						// Add the issue to the new parent if parent issue already expanded
						const newVariable = getVariables(getVariablesInput(parentIssue.issueId));
						const newNode = generateIssueWithUpdatedParent(issue, parentIssue, view);
						addToCache({
							query: cacheQuery,
							variables: newVariable,
							newNodes: [originalRank ? getOptimisticRanked(newNode, originalRank) : newNode],
						});
					} else {
						shouldAddToCache.current = true;
					}
				}

				// update child count of prev parent
				if (oldParentId && removeChildFromParent) {
					const grandParentId = issuesMap.get(oldParentId)?.parentIssueId;
					const queryVariables = getVariables(getVariablesInput(grandParentId));

					updateItemsInCache<IssueCacheNode, IssueCacheValue>({
						query: cacheQuery,
						variables: queryVariables,
						issueIds: [String(oldParentId)],
						updateItem: (existingParentIssue) => removeChildFromParent(existingParentIssue, 1),
					});
				}
			}
		},
		[
			getVariablesInput,
			getCachedIssueById,
			cacheQuery,
			removeFromCache,
			childrenQueryData,
			removeChildFromParent,
			view,
			addToCache,
			issuesMap,
			updateItemsInCache,
		],
	);

	const unlinkParentIssueCache = useCallback(
		({
			issueKey,
			oldParentId,
			updateRank,
		}: {
			issueKey: string;
			oldParentId?: string;
			updateRank?: string;
		}) => {
			const variables = getVariables(getVariablesInput(oldParentId));
			const issue = getCachedIssueById({
				issueId: issueKey,
				query: cacheQuery,
				variables,
			});

			// Remove the issue from the old parent
			removeFromCache({
				query: cacheQuery,
				variables,
				issueId: Number(issueKey),
			});

			// update child count of prev parent
			if (oldParentId && removeChildFromParent) {
				const grandParentId = issuesMap.get(oldParentId)?.parentIssueId;
				const queryVariables = getVariables(getVariablesInput(grandParentId));
				updateItemsInCache<IssueCacheNode, IssueCacheValue>({
					query: cacheQuery,
					variables: queryVariables,
					issueIds: [String(oldParentId)],
					updateItem: (parentIssue) => removeChildFromParent(parentIssue, 1),
				});
			}

			const reparentIssueVariable = getVariables(getVariablesInput());
			if (issue) {
				const newNode = generateIssueWithUpdatedParent(issue, undefined, view);
				// Add issue to the top level
				addToCache({
					query: cacheQuery,
					variables: reparentIssueVariable,
					newNodes: [updateRank ? getOptimisticRanked(newNode, updateRank) : newNode],
				});
			}
		},
		[
			getVariablesInput,
			getCachedIssueById,
			cacheQuery,
			removeFromCache,
			removeChildFromParent,
			issuesMap,
			updateItemsInCache,
			view,
			addToCache,
		],
	);

	const revertOptimisticCacheUpdate = useCallback(
		({ originalParentId, parentIssue, issueKey, originalRank }: RevertData) => {
			const originalParent = originalParentId && issuesMap.get(originalParentId);
			if (originalParent) {
				updateIssueParentCache({
					oldParentId: parentIssue?.issueId,
					issueKey,
					parentIssue: originalParent,
				});
			} else if (originalRank && parentIssue) {
				unlinkParentIssueCache({
					oldParentId: parentIssue.issueId,
					issueKey,
					updateRank: originalRank,
				});
			}
		},
		[issuesMap, unlinkParentIssueCache, updateIssueParentCache],
	);

	useEffect(() => {
		if (
			revertData &&
			revertData.parentIssue?.issueId &&
			childrenQueryData[revertData.parentIssue.issueId] &&
			childrenQueryData[revertData.parentIssue.issueId].loading === false
		) {
			const { originalParentId, parentIssue, issueKey, originalRank } = revertData;

			originalRank && setOptimisticRank(originalRank);
			revertOptimisticCacheUpdate({
				originalParentId,
				parentIssue,
				issueKey,
				originalRank,
			});

			setRevertData(null);
		}
	}, [childrenQueryData, revertData, revertOptimisticCacheUpdate]);

	const updateIssueParent = useCallback(
		async ({
			issueKey,
			parentIssue,
			rankRequest,
			oldParentIssueId,
			isMeatballsAction,
		}: UpdateParentAndRankIssueItem) => {
			!shouldOnlyUpdateCache && startExperience();
			const updateRank = getRankPosition(rankRequest);

			if (updateRank) setOptimisticRank(updateRank);

			const issue = issuesMap.get(String(issueKey));
			const originalRank = issue?.rank;
			const oldParentId = oldParentIssueId ?? issueParentMap?.get(issueKey);
			updateIssueParentCache({ oldParentId, issueKey, parentIssue, isMeatballsAction });

			if (shouldOnlyUpdateCache) {
				await Promise.resolve();
				return { updateStatus: SUCCEEDED };
			}
			try {
				await updateIssueField({
					issueKey,
					fieldKey: PARENT_TYPE,
					newValue: {
						id: String(parentIssue.issueId),
					},
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: {
						message: 'update issue parent failed',
						parentLevel: parentIssue?.issueType?.hierarchyLevel,
						issueLevel: issue?.issueType?.hierarchyLevel,
						view,
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				setRevertData({
					originalParentId: oldParentId,
					parentIssue,
					issueKey,
					originalRank,
				});

				return { updateStatus: FAILED };
			}
			return { updateStatus: SUCCEEDED };
		},
		[
			shouldOnlyUpdateCache,
			startExperience,
			getRankPosition,
			issuesMap,
			issueParentMap,
			updateIssueParentCache,
			updateIssueField,
			view,
		],
	);

	const unlinkParentIssue = useCallback(
		async ({ issueKey, rankRequest, oldParentIssueId }: UnlinkAndReRankIssueItem) => {
			!shouldOnlyUpdateCache && startExperience();
			const oldParentId = oldParentIssueId || issueParentMap?.get(issueKey);

			const updateRank = getRankPosition(rankRequest);
			const originalRank = issuesMap.get(String(issueKey))?.rank;

			unlinkParentIssueCache({ issueKey, oldParentId, updateRank });

			if (shouldOnlyUpdateCache) {
				await Promise.resolve();
				return { updateStatus: SUCCEEDED };
			}

			try {
				await updateIssueField({
					issueKey,
					fieldKey: PARENT_TYPE,
					newValue: {
						id: null,
					},
				});

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				const issue = issuesMap.get(String(issueKey));
				fireErrorAnalytics({
					meta: {
						id: ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: {
						message: 'unlink issue parent failed',
						issueLevel: issue?.issueType?.hierarchyLevel,
						view,
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				if (originalRank) {
					setOptimisticRank(originalRank);
					revertOptimisticCacheUpdate({
						originalParentId: oldParentId,
						issueKey,
					});
				}
				return { updateStatus: FAILED };
			}

			return { updateStatus: SUCCEEDED };
		},
		[
			shouldOnlyUpdateCache,
			startExperience,
			issueParentMap,
			getRankPosition,
			issuesMap,
			unlinkParentIssueCache,
			updateIssueField,
			view,
			revertOptimisticCacheUpdate,
		],
	);

	return {
		updateIssueParent,
		unlinkParentIssue,
	};
};
